import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../layout/LanguageSwitcher';
import logo from '../../assests/ampera_logo.png';
import mountains from '../../assets/home/banner-mountains.webp';
import footerHills from '../../assets/footer-hills.png';

/**
 * The frame every public QR page sits in — the form itself, and the
 * loading / invalid / password / thank-you states.
 *
 * A guest reaches these pages by scanning a sticker on a table, with no idea
 * what they are about to open, so the page has to introduce itself: the real
 * mark, the landscape, and the language control right where a stranger looks
 * for it.
 */
export default function PublicFormShell({ title, subtitle, description, badge, children, narrow = false }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-brand-cream font-sans">
      <div className={`mx-auto px-4 py-6 sm:py-10 ${narrow ? 'max-w-lg' : 'max-w-2xl'}`}>
        {/* Header */}
        <div className="relative overflow-hidden rounded-3xl pb-6 shadow-sm ring-1 ring-brand-line sm:pb-8">
          <img
            src={mountains}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 h-full w-full object-cover"
          />
          {/* A near-even veil rather than a side-weighted one: Arabic mirrors
              the whole layout, so any wash that protects one side leaves the
              type stranded on the photo in the other direction. This reads the
              same whichever way the page runs. */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(180deg, rgba(250,246,240,0.94) 0%, rgba(250,246,240,0.86) 55%, rgba(250,246,240,0.74) 100%)' }}
          />

          <div className="relative p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              {/* Tapping the mark is the only way back to the site from a
                  scanned link, so it is a real link, not decoration. */}
              {/* The round mark is the only logo asset the repo has; the
                  wordmark beside it is set in the brand serif rather than
                  invented as an image, so it stays crisp and translatable. */}
              <Link
                to="/"
                aria-label={t('publicForm.goHome')}
                className="group inline-flex items-center gap-3 transition-opacity hover:opacity-80"
              >
                <img src={logo} alt="" className="h-11 w-auto sm:h-12" />
                <span className="leading-none">
                  <span className="block text-[9px] font-semibold uppercase tracking-[0.28em] text-brand-clay/80 sm:text-[10px]">
                    Warung Nasi
                  </span>
                  <span className="mt-0.5 block font-serif text-2xl font-bold tracking-tight text-brand-clay sm:text-[28px]">
                    AMPERA
                  </span>
                  <span className="mt-0.5 block text-[8.5px] font-medium uppercase tracking-[0.2em] text-brand-body/70 sm:text-[9px]">
                    — Sejak 1963 —
                  </span>
                </span>
              </Link>
              <LanguageSwitcher />
            </div>

            <p className="mt-4 font-script text-xl leading-tight text-brand-moss sm:text-2xl">
              {t('publicForm.tagline')}
            </p>

            <h1 className="mt-2 font-serif text-3xl font-bold leading-tight text-brand-ink sm:text-4xl">
              {title}
            </h1>
            {subtitle && <p className="mt-1 text-base font-medium text-brand-body sm:text-lg">{subtitle}</p>}
            {description && <p className="mt-2 max-w-md text-sm leading-relaxed text-brand-body/90">{description}</p>}

            {badge && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/70 px-3.5 py-1.5 text-sm font-semibold text-brand-moss ring-1 ring-brand-moss/20 backdrop-blur">
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="4" y="11" width="16" height="10" rx="2" />
                  <path d="M8 11V7a4 4 0 118 0v4" />
                </svg>
                {badge}
              </div>
            )}
          </div>
        </div>

        <div className="relative -mt-5 sm:-mt-7">{children}</div>

        {/* Footer. web-footer.png has the Indonesian wording baked across its
            middle, which would contradict the language switcher above, so
            footer-hills.png is that artwork cropped below the last row of ink
            (measured, not eyeballed). The words are real text and translate. */}
        <div className="mt-10 text-center">
          <p className="font-script text-2xl text-brand-ink">
            {t('publicForm.thanks')} <span className="text-brand-clay">♡</span>
          </p>
          <p className="mt-1 text-sm text-brand-body">{t('publicForm.thanksTagline')}</p>
          <img
            src={footerHills}
            alt=""
            aria-hidden="true"
            className="mt-4 w-full select-none"
          />
        </div>
      </div>
    </div>
  );
}
